import { EditRounded, Search } from "@mui/icons-material";
import {
  InputAdornment,
  Paper,
  Select,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  GridColDef,
  GridActionsCellItem,
  DataGrid,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import "./styles.scss";
import { UserDetailsType } from "../../types/genericType";

type UsersTableProps = {
  data: Array<UserDetailsType>;
  onEditClick: Function;
};

const UsersTable = (props: UsersTableProps) => {
  const { data, onEditClick } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState(data);
  const [seletedStatus, setSelectedStatus] = useState("active");

  const handleSearch = (event: any) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  useEffect(() => {
    const termParts = searchTerm.trim().toLowerCase().split(/\s+/);
    const filteredData = data.filter((row) => {
      const matchedTerm = termParts.every((term) =>
        Object.values(row).some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(term)
        )
      );

      const matchesStatus =
        seletedStatus === "*" ||
        (row.isActive ? "active" : "inactive") === seletedStatus;

      return matchedTerm && matchesStatus;
    });

    setFilteredRows(filteredData);
  }, [data, searchTerm, seletedStatus]);

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        hideable: false,
        minWidth: 440,
        sortable: false,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          const { firstName, lastName } = params.row;
          return `${firstName && firstName} ${lastName && lastName}`;
        },
      },
      {
        field: "email",
        headerName: "Email",
        hideable: false,
        minWidth: 440,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "status",
        headerName: "Status",
        hideable: false,
        minWidth: 120,
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return params.row.isActive ? "Active" : "Inactive";
        },
      },
      {
        field: "role",
        headerName: "Type",
        hideable: false,
        minWidth: 180,
        sortable: false,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        valueGetter: (params) => {
          return params.row.role ?? "-";
        },
      },
      {
        field: "id",
        headerName: "Edit",
        sortable: false,
        width: 30,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params: GridValueGetterParams) => [
          <GridActionsCellItem
            icon={<EditRounded sx={{ fontSize: "20px" }} />}
            label=""
            onClick={() => {
              onEditClick(params.row.id);
            }}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Paper elevation={3} className="users-table">
      <Stack
        spacing={2}
        direction="row"
        className="search-filter"
        padding="20px"
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          className="search-field"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <FormControl style={{ width: 200 }}>
          <InputLabel id="status_label" shrink>
            Status
          </InputLabel>

          <Select
            labelId="status_label"
            label="Status"
            value={seletedStatus}
            onChange={(event: SelectChangeEvent) =>
              setSelectedStatus(event.target.value as string)
            }
            sx={{
              height: "40px", // Adjust this value as needed
              padding: "0 14px", // Adjust this value as needed
              "& .MuiSelect-select": {
                padding: "0px 0", // Adjust this value as needed
              },
            }}
          >
            <MenuItem value="*">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {filteredRows.length > 0 ? (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          rowSelection={false}
          className="datagrid"
        />
      ) : (
        <div className="no-results">No results found</div>
      )}
    </Paper>
  );
};

export default UsersTable;
